// Lib
import ResizeObserver from 'resize-observer-polyfill';
import { RefObject, useCallback, useEffect, useLayoutEffect, useState } from 'react';

// Types
import { RectSize } from '../../../common/maths/geometry/rect/rectTypes';

const EMPTY_SIZE: RectSize = {
    width: 0,
    height: 0,
};

const getElementSize = (element: HTMLElement): RectSize => {
    const { width, height } = element.getBoundingClientRect();

    return { width, height };
};

const hasSizeChanged = (currentSize: RectSize, nextSize: RectSize) =>
    currentSize.width !== nextSize.width || currentSize.height !== nextSize.height;

/**
 * Measures an element's rendered width and height.
 *
 * ResizeObserver catches element size changes. The getBoundingClientRect read is batched
 * into requestAnimationFrame so several resize events produce one React state update.
 *
 * The first measurement happens before the first paint, so callers never lay out against the initial
 * zero size. Pass `remeasureKey` to get the same treatment when the observed element's *content* is
 * swapped rather than resized (e.g. navigating to a different element) — the batched observer path
 * would otherwise only report the new size a frame later, against the previous content's size.
 */
export const useMeasuredElementRect = (elementRef: RefObject<HTMLElement>, remeasureKey?: unknown): RectSize => {
    const [size, setSize] = useState(EMPTY_SIZE);

    const measureElement = useCallback(() => {
        const element = elementRef.current;

        if (!element) return;

        const nextSize = getElementSize(element);

        setSize((currentSize) => (hasSizeChanged(currentSize, nextSize) ? nextSize : currentSize));
    }, [elementRef]);

    // Runs after React commits (on mount, and again whenever the content is keyed as swapped) but before
    // the browser paints, so the size is measured in time for the first rendered frame.
    useLayoutEffect(() => {
        measureElement();
    }, [measureElement, remeasureKey]);

    useEffect(() => {
        const element = elementRef.current;

        if (!element) return undefined;

        let animationFrameId: number | null = null;
        const requestMeasurement = () => {
            animationFrameId = requestAnimationFrame(measureElement);
        };

        const resizeObserver = new ResizeObserver(requestMeasurement);

        resizeObserver.observe(element);
        requestMeasurement();

        return () => {
            resizeObserver.disconnect();

            if (animationFrameId) cancelAnimationFrame(animationFrameId);
        };
    }, [elementRef, measureElement]);

    return size;
};
